import { z } from 'zod/v3';
import { DocDifficultySchema, TopicStatusSchema, IconCategorySchema, IconTypeSchema, TopicTypeSchema } from '../generated/enums';
import { DocDTOSchema } from './doc.dto';
import { OpenApiConfigSchema, ChangelogConfigSchema } from './api-import.dto';

// Base section configuration (shared fields)
export const BaseSectionConfigSchema = z.object({
  icon: z.string().optional(),
  color: z.string().optional(),
  description: z.string().optional(),
  allowManualTopics: z.boolean().optional(),
  allowImports: z.boolean().optional(),
  defaultTemplate: z.string().optional(),
  sortOrder: z.enum(['manual', 'date', 'alphabetical']).optional(),
  allowedChildTypes: z.array(TopicTypeSchema).optional(),
  defaultExpanded: z.boolean().optional(),
  collapsible: z.boolean().optional(),
});

// Generic section configuration
export const GenericSectionConfigSchema = BaseSectionConfigSchema.extend({
  sectionType: z.literal('generic'),
});

// Guides section configuration
export const GuidesSectionConfigSchema = BaseSectionConfigSchema.extend({
  sectionType: z.literal('guides'),
});

// Changelog section configuration
export const ChangelogSectionConfigSchema = BaseSectionConfigSchema.extend({
  sectionType: z.literal('changelog'),
  githubRepo: z.string(),
  branch: z.string().default('main'),
  changelogStrategy: z.enum(['release', 'monthly', 'quarterly', 'yearly']).default('release'),
  changelogTemplateId: z.string().optional(),
  connectionId: z.string().optional(), // Reference to IntegrationConnection
});

// API section configuration
export const ApiSectionConfigSchema = BaseSectionConfigSchema.extend({
  sectionType: z.literal('api'),
  sourceUrl: z.string().optional(),
  sourceFile: z.string().optional(),
  openApiSpec: z.any().optional(),
  version: z.string().optional(),
  servers: z.array(z.any()).optional(),
  tags: z.array(z.any()).optional(),
});

// Section configuration schema - discriminated union with optional fallback
export const SectionConfigSchema = z.union([
  GenericSectionConfigSchema,
  GuidesSectionConfigSchema,
  ChangelogSectionConfigSchema,
  ApiSectionConfigSchema,
  // Fallback for sections without explicit type (backward compatibility)
  BaseSectionConfigSchema,
]);


// Get topic by ID
export const GetTopicByIdDTOSchema = z.object({
  id: z.string(),
  workspaceId: z.string().optional(),
  branchId: z.string().optional(),
}).strict();

// Create topic
export const CreateTopicDTOSchema = z
  .object({
    workspaceId: z.string(),
    branchId: z.string().optional(),
    flowId: z.string().optional(),
    title: z.string().min(1),
    description: z.string().optional(),
    parentId: z.string().nullish(),
    customSlug: z.string().optional(),
    type: TopicTypeSchema.optional(),
    apiConfig: OpenApiConfigSchema.optional(),
    changelogConfig: ChangelogConfigSchema.optional(),
    sectionConfig: SectionConfigSchema.optional(),
    isDefaultSection: z.boolean().optional(),
    sectionOrder: z.number().optional(),
    difficulty: DocDifficultySchema.optional(),
    estimatedReadMinutes: z.number().optional(),
    tags: z.array(z.string()).optional(),
    icon: z.string().optional(),
    iconCategory: IconCategorySchema.optional(),
    iconType: IconTypeSchema.optional(),
    iconColor: z.string().optional(),
    createInitialDoc: z.boolean().optional().default(false),
    docTemplateId: z.string().optional(),
  })
  .strict()
  .superRefine((data, ctx) => {
    if (data.type === 'GROUP' && data.createInitialDoc) {
      ctx.addIssue({
        code: 'custom',
        path: ['createInitialDoc'],
        message: 'Group topics cannot create initial docs',
      });
    }

    if (data.type === 'GROUP' && !data.parentId) {
      ctx.addIssue({
        code: 'custom',
        path: ['parentId'],
        message: 'Group topics must belong to a section',
      });
    }
  });

// Update topic
export const UpdateTopicDTOSchema = z.object({
  id: z.string(),
  branchId: z.string().optional(),
  data: z.object({
    title: z.string().optional(),
    description: z.string().optional(),
    parentId: z.string().nullish().optional(),
    order: z.number().optional(),
    status: TopicStatusSchema.optional(),
    flowId: z.string().optional(),
    sectionConfig: SectionConfigSchema.optional(),
    isDefaultSection: z.boolean().optional(),
    sectionOrder: z.number().optional(),
    difficulty: DocDifficultySchema.optional(),
    estimatedReadMinutes: z.number().optional(),
    tags: z.array(z.string()).optional(),
    // Page Options
    pageTitle: z.string().optional(),
    pageDescription: z.string().optional(),
    pageKeywords: z.array(z.string()).optional(),
    showInSite: z.boolean().optional(),
    showInNavigation: z.boolean().optional(),
    showTableOfContents: z.boolean().optional(),
    customSlug: z.string().optional(),
    pageIcon: z.string().optional(),
    pageColor: z.string().optional(),
    socialImage: z.string().optional(),
    noIndex: z.boolean().optional(),
    icon: z.string().optional(),
    iconCategory: IconCategorySchema.optional(),
    iconType: IconTypeSchema.optional(),
    iconColor: z.string().optional(),
  }).strict(),
}).strict();

// Get topics query
export const GetTopicsQueryDTOSchema = z.object({
  workspaceId: z.string(),
  branchId: z.string().optional(),
  limit: z.number().optional().default(20),
  cursor: z.string().optional(),
  parentId: z.string().optional(),
  type: TopicTypeSchema.optional(),
}).strict();

// Generate topics options
export const GenerateTopicsOptionsDTOSchema = z.object({
  maxTopicDepth: z.number().optional(),
  minStepsPerTopic: z.number().optional(),
  style: z.enum(['concise', 'detailed']).optional(),
  language: z.string().optional(),
}).strict();

// Generate topics input
export const GenerateTopicsDTOSchema = z.object({
  workspaceId: z.string(),
  options: GenerateTopicsOptionsDTOSchema.optional(),
}).strict();

// Suggest topics input
export const SuggestTopicsDTOSchema = z.object({
  workspaceId: z.string(),
  parentTopicId: z.string().optional(),
}).strict();

// Process step input
export const ProcessStepDTOSchema = z.object({
  workspaceId: z.string(),
  stepId: z.string(),
}).strict();

// Add flow to topic input
export const AddFlowToTopicDTOSchema = z.object({
  topicId: z.string(),
  flowId: z.string(),
}).strict();

// Get topic hierarchy input
export const GetTopicHierarchyDTOSchema = z.object({
  workspaceId: z.string(),
  branchId: z.string().optional(),
  parentTopicId: z.string().optional(),
}).strict();

export const BranchTopicDTOSchema = z.object({
  id: z.string(),
  topicId: z.string(),
  branchId: z.string(),
  canonicalSlug: z.string().optional(),
  currentDocId: z.string().optional(),
  currentDoc: DocDTOSchema.optional(),
  pageIcon: z.string().nullable().optional(),
}).strict();

// Base topic schema
export const TopicDTOSchema = z.object({
  id: z.string(),
  title: z.string(),
  description: z.string().nullable(),
  workspaceId: z.string(),
  parentId: z.string().nullable(),
  order: z.number(),
  createdAt: z.date(),
  updatedAt: z.date(),
  status: TopicStatusSchema,
  stepIds: z.array(z.string()),
  canonicalSlug: z.string().optional(),
  isSuggested: z.boolean().optional(),
  type: TopicTypeSchema.optional(),
  sectionConfig: SectionConfigSchema.nullable().optional(),
  isDefaultSection: z.boolean().optional(),
  sectionOrder: z.number().nullable().optional(),
  difficulty: DocDifficultySchema.optional(),
  estimatedReadMinutes: z.number().optional(),
  tags: z.array(z.string()).optional(),
  branchTopics: z.array(BranchTopicDTOSchema).optional(),
  docId: z.string().optional(),
  // Page Options
  pageTitle: z.string().nullable().optional(),
  pageDescription: z.string().nullable().optional(),
  pageKeywords: z.array(z.string()).optional(),
  showInSite: z.boolean().optional(),
  showInNavigation: z.boolean().optional(),
  showTableOfContents: z.boolean().optional(),
  customSlug: z.string().nullable().optional(),
  pageIcon: z.string().nullable().optional(),
  pageColor: z.string().nullable().optional(),
  socialImage: z.string().nullable().optional(),
  noIndex: z.boolean().optional(),
  icon: z.string().nullable().optional(),
  iconCategory: IconCategorySchema.nullable().optional(),
  iconType: IconTypeSchema.nullable().optional(),
  iconColor: z.string().nullable().optional(),
}).strict();

// Export types
export type SectionConfig = z.infer<typeof SectionConfigSchema>;
export type BaseSectionConfig = z.infer<typeof BaseSectionConfigSchema>;
export type GenericSectionConfig = z.infer<typeof GenericSectionConfigSchema>;
export type GuidesSectionConfig = z.infer<typeof GuidesSectionConfigSchema>;
export type ChangelogSectionConfig = z.infer<typeof ChangelogSectionConfigSchema>;
export type ApiSectionConfig = z.infer<typeof ApiSectionConfigSchema>;
export type TopicDTO = z.infer<typeof TopicDTOSchema>;
export type GetTopicByIdDTO = z.infer<typeof GetTopicByIdDTOSchema>;
export type CreateTopicDTO = z.infer<typeof CreateTopicDTOSchema>;
export type UpdateTopicDTO = z.infer<typeof UpdateTopicDTOSchema>;
export type GetTopicsQueryDTO = z.infer<typeof GetTopicsQueryDTOSchema>;
export type GenerateTopicsOptionsDTO = z.infer<typeof GenerateTopicsOptionsDTOSchema>;
export type GenerateTopicsDTO = z.infer<typeof GenerateTopicsDTOSchema>;
export type SuggestTopicsDTO = z.infer<typeof SuggestTopicsDTOSchema>;
export type ProcessStepDTO = z.infer<typeof ProcessStepDTOSchema>;
export type AddFlowToTopicDTO = z.infer<typeof AddFlowToTopicDTOSchema>;
export type GetTopicHierarchyDTO = z.infer<typeof GetTopicHierarchyDTOSchema>;
export type BranchTopicDTO = z.infer<typeof BranchTopicDTOSchema>;